import { CloseCircleOutlined } from '@ant-design/icons'
import {
  Box,
  CircularProgress,
  Grid2 as Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import useAuth from 'hooks/useAuth'
import { usePeriodOverviews } from 'hooks/useDashboard'
import useLocalStorage from 'hooks/useLocalStorage'
import { usePeriods } from 'hooks/usePeriods'
import React, { useEffect, useRef, useState, useTransition } from 'react'
import type { Period } from 'types/periods'
import { createIdToPeriodFromList } from 'types/periods'
import ApprovalsTile from './ApprovalsTile'
import OnboardOverviews from './OnboardOverviews'
import TeamEmployeeOverviews from './TeamEmployeeOverviews'
import TeamOverview from './TeamOverview'

const ManagerDashboard: React.FC = () => {
  const { token, profile } = useAuth()
  const theme = useTheme()
  const [selectedOrganizations, setSelectedOrganizations] = useLocalStorage<string[]>(
    'dashboard-selected-organizations',
    [],
    profile?.user?.id
  )
  const [selectedPeriods, setSelectedPeriods] = useLocalStorage<string[]>(
    'dashboard-selected-periods',
    [],
    profile?.user?.id
  )
  const [periods, setPeriods] = useState<Period[]>([])
  const [isPending, startTransition] = useTransition()
  const [isInitialized, setIsInitialized] = useState(false)
  const [view, setView] = useState<'period' | 'annual'>('period')
  const filtersRef = useRef<HTMLDivElement>(null)
  const [filtersHeight, setFiltersHeight] = useState<number | undefined>(undefined)

  useEffect(() => {
    const node = filtersRef.current
    if (!node) return
    const observer = new ResizeObserver((entries) => {
      setFiltersHeight(entries[0].target.getBoundingClientRect().height)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (profile && profile.roles.length > 0 && !isInitialized) {
      const allOrganizationIds = profile.roles.map((role) => role.organization.id)
      if (selectedOrganizations.length === 0) {
        setSelectedOrganizations(allOrganizationIds)
      }
      setIsInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, isInitialized])

  const { periods: fetchedPeriods, isLoadingPeriods } = usePeriods(
    token!,
    selectedOrganizations[0] || '',
    !!token && selectedOrganizations.length > 0
  )

  useEffect(() => {
    console.log('isLoadingPeriods', isLoadingPeriods)
    console.log('fetchedPeriods', fetchedPeriods)
    if (fetchedPeriods) {
      // Filter to only show periods that have started (pastOnly logic)
      const now = new Date()
      const filteredPeriods = (fetchedPeriods.periods || []).filter((period) => {
        const startDate = new Date(period.startDate)
        return startDate <= now
      })

      // Sort filtered periods by end date in descending order
      const sortedPeriods = [...filteredPeriods].sort(
        (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
      )
      setPeriods(sortedPeriods)

      // Validate that currently selected periods are still valid (exist in any level of filtered periods)
      const allPeriodIds = new Set<string>()
      const collectIds = (p: Period) => {
        if (p.id) allPeriodIds.add(p.id)
        p.children?.forEach(collectIds)
      }
      sortedPeriods.forEach(collectIds)
      const invalidSelections = selectedPeriods.filter((id) => !allPeriodIds.has(id))

      if (invalidSelections.length > 0) {
        console.log('Clearing invalid period selections:', invalidSelections)
        setSelectedPeriods([])
      }

      // Select the first period if no valid selection exists
      if (sortedPeriods.length > 0 && (selectedPeriods.length === 0 || invalidSelections.length > 0)) {
        console.log('Setting selected period to', sortedPeriods[0].id)
        setSelectedPeriods([sortedPeriods[0].id!])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedPeriods, isLoadingPeriods])

  // Compute rootPeriodId from selected period (which may be a sub-period)
  const allPeriodsMap = React.useMemo(() => createIdToPeriodFromList(periods), [periods])
  const rootPeriodId = React.useMemo(() => {
    const selectedPeriodId = selectedPeriods[0] || ''
    if (!selectedPeriodId) return ''
    const period = allPeriodsMap[selectedPeriodId]
    return period?.rootPeriodId || selectedPeriodId
  }, [selectedPeriods, allPeriodsMap])

  const { periodOverviews, isLoading: isLoadingOverviews } = usePeriodOverviews(
    token!,
    {
      rootPeriodId,
      organizationIds: selectedOrganizations,
    },
    !!token && !!rootPeriodId && selectedOrganizations.length > 0 && isInitialized
  )

  const handleOrganizationChange = (newSelectedOrganizations: string[]) => {
    startTransition(() => {
      setSelectedOrganizations(newSelectedOrganizations)
    })
  }

  const handlePeriodChange = (periodId: string) => {
    startTransition(() => {
      setSelectedPeriods(periodId ? [periodId] : [])
    })
  }

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {/* Main Content - Second on mobile, left column on desktop */}
      <Grid size={{ xs: 12, md: 9 }} sx={{ order: { xs: 1, md: 0 } }}>
        {isLoadingOverviews || isLoadingPeriods || isPending ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TeamOverview
              userPeriodOverviews={periodOverviews?.userPeriodOverviews || []}
              view={view}
              selectedPeriod={selectedPeriods[0] || ''}
              periods={periods}
              minHeight={filtersHeight}
            />
            <TeamEmployeeOverviews
              userPeriodOverviews={periodOverviews?.userPeriodOverviews || []}
              theme={theme}
              view={view}
              selectedPeriod={selectedPeriods[0] || ''}
              periods={periods}
            />
          </>
        )}
      </Grid>

      {/* Right Sidebar - Filters first on mobile, then Status/Approvals; All in right sidebar on desktop */}
      <Grid size={{ xs: 12, md: 3 }} sx={{ order: { xs: 0, md: 1 } }}>
        {/* Filters */}
        <Paper
          ref={filtersRef}
          id="manager-dashboard-filters"
          sx={{ mb: 2, pt: { xs: 3, sm: 4 }, pr: { xs: 3, sm: 4 }, pb: { xs: 3, sm: 4 } }}
        >
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(e, newView) => {
                if (newView !== null) {
                  startTransition(() => {
                    setView(newView)
                  })
                }
              }}
              aria-label="View toggle"
              size="small"
              sx={{ height: 32, width: '100%' }}
            >
              <ToggleButton value="period" aria-label="Period Progress" sx={{ width: '50%' }}>
                Period Progress
              </ToggleButton>
              <ToggleButton value="annual" aria-label="Annual Progress" sx={{ width: '50%' }}>
                Annual Progress
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid size={{ xs: 10, sm: 11 }}>
                <MultiOrganizationSelector
                  key="organization-selector"
                  userProfile={profile!}
                  selectedOrganizationIds={selectedOrganizations}
                  handleChange={handleOrganizationChange}
                  labelBackgroundColor={theme.palette.background.paper}
                />
              </Grid>
              <Grid size={{ xs: 2, sm: 1 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Reset Organization Filter">
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => {
                      setSelectedOrganizations([])
                    }}
                  >
                    <CloseCircleOutlined />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
          <Box>
            <Grid container spacing={1} alignItems="center">
              <Grid size={{ xs: 10, sm: 11 }}>
                <SinglePeriodSelector
                  key="period-selector"
                  periods={periods}
                  value={selectedPeriods[0] || ''}
                  onChange={handlePeriodChange}
                  sortOrder="desc"
                  timeFilter="pastOnly"
                  variant="outlined"
                  shrink
                  pill
                  label="Select Period"
                  placeholder="Select Period"
                  labelBackgroundColor={theme.palette.background.paper}
                />
              </Grid>
              <Grid size={{ xs: 2, sm: 1 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Reset Period Filter">
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => {
                      setSelectedPeriods([])
                    }}
                  >
                    <CloseCircleOutlined />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Status Overview and Approvals - Below filters in same column */}
        {periodOverviews && (
          <Paper sx={{ mb: 2 }}>
            <OnboardOverviews
              planApprovalStats={periodOverviews.planApprovalStats}
              targetMetricCounts={periodOverviews.targetMetricCounts}
              approvedComponentCounts={periodOverviews.approvedComponentCounts}
              accrualCounts={periodOverviews.accrualCounts}
              theme={theme}
            />
          </Paper>
        )}

        <Paper sx={{ mb: 2 }}>
          <ApprovalsTile theme={theme} />
        </Paper>
      </Grid>
    </Grid>
  )
}

export default ManagerDashboard
